import store from "@/store";
import dayjs from "dayjs";

function pluralizeLabel(label) {
  if (!label || typeof label !== "string") return label;
  const lower = label.toLowerCase();

  const noPluralize = ["family", "team", "staff", "people"];
  if (noPluralize.includes(lower) || lower.endsWith("s")) {
    return label;
  }

  const irregulars = {
    person: "people",
    child: "children",
    man: "men",
    woman: "women",
    mouse: "mice",
  };
  if (irregulars[lower]) {
    const out = irregulars[lower];
    if (label.charAt(0) === label.charAt(0).toUpperCase()) {
      return out.charAt(0).toUpperCase() + out.slice(1);
    }
    return out;
  }

  if (lower.endsWith("y")) {
    return label.slice(0, -1) + "ies";
  }
  return label + "s";
}

const filters = {
  date(value, format) {
    if (!value) return "";
    return dayjs(value).format(format);
  },

  nl2br(value) {
    const result = value.split("\n").join("<br>");
    return result;
  },

  maxEllipsis(value, max) {
    const nlNumber = value.split("\n").length - 1;
    const hrNumber = value.split("-------------------------------").length - 1;
    const maxLength = nlNumber * 2 + hrNumber * 31;
    if (value.length <= max + maxLength) {
      return value;
    }
    let result = `${value.substring(0, max + maxLength)}`;
    if (result.lastIndexOf("\\") === result.length - 1) {
      result = `${result}n`;
    }
    result = `${result}...`;
    return result;
  },

  slash2hr(value) {
    const result = value.split("-------------------------------").join("<hr>");
    return result;
  },

  capitalize(value) {
    if (!value) return "";
    value = value.toString();
    return value.charAt(0).toUpperCase() + value.slice(1);
  },

  uppercase(value) {
    if (!value) return "";
    value = value.toString();
    return value.toUpperCase();
  },

  customerLabel(
    type = "singular",
    capitalize = false,
    prefix = "",
    suffix = ""
  ) {
    if (type && typeof type === "object") {
      const opts = type || {};
      type = opts.type || "singular";
      capitalize = opts.capitalize || false;
      prefix = opts.prefix || "";
      suffix = opts.suffix || "";
    }

    let label = "client";
    try {
      const cp = store.state?.settings?.companyProfile;
      if (
        cp?.customer_name &&
        typeof cp.customer_name === "string" &&
        cp.customer_name.trim() !== ""
      ) {
        label = cp.customer_name.trim();
      }
    } catch (e) {
      // keep default
    }

    let result = label;
    if (type === "plural") {
      result = pluralizeLabel(result);
    }

    if (capitalize) {
      if (result.length > 0) {
        result = result.charAt(0).toUpperCase() + result.slice(1);
      }
    } else {
      if (result.length > 0) {
        result = result.charAt(0).toLowerCase() + result.slice(1);
      }
    }

    return `${prefix || ""}${result}${suffix || ""}`;
  },

  providerLabel(
    type = "singular",
    capitalize = false,
    prefix = "",
    suffix = ""
  ) {
    if (type && typeof type === "object") {
      const opts = type || {};
      type = opts.type || "singular";
      capitalize = opts.capitalize || false;
      prefix = opts.prefix || "";
      suffix = opts.suffix || "";
    }

    let label = "provider";
    try {
      const cp = store.state?.settings?.companyProfile;
      if (
        cp?.provider_name &&
        typeof cp.provider_name === "string" &&
        cp.provider_name.trim() !== ""
      ) {
        label = cp.provider_name.trim();
      }
    } catch (e) {
      // keep default
    }

    let result = label;
    if (type === "plural") {
      result = pluralizeLabel(result);
    }

    if (capitalize) {
      if (result.length > 0) {
        result = result.charAt(0).toUpperCase() + result.slice(1);
      }
    } else {
      if (result.length > 0) {
        result = result.charAt(0).toLowerCase() + result.slice(1);
      }
    }

    return `${prefix || ""}${result}${suffix || ""}`;
  },

  clinicLabel(type = "singular", capitalize = false, prefix = "", suffix = "") {
    if (type && typeof type === "object") {
      const opts = type || {};
      type = opts.type || "singular";
      capitalize = opts.capitalize || false;
      prefix = opts.prefix || "";
      suffix = opts.suffix || "";
    }

    let label = "clinic";
    try {
      const cp = store.state?.settings?.companyProfile;
      if (
        cp?.clinic_name &&
        typeof cp.clinic_name === "string" &&
        cp.clinic_name.trim() !== ""
      ) {
        label = cp.clinic_name.trim();
      }
    } catch (e) {
      // keep default
    }

    let result = label;
    if (type === "plural") {
      result = pluralizeLabel(result);
    }

    if (capitalize) {
      if (result.length > 0) {
        result = result.charAt(0).toUpperCase() + result.slice(1);
      }
    } else {
      if (result.length > 0) {
        result = result.charAt(0).toLowerCase() + result.slice(1);
      }
    }

    return `${prefix || ""}${result}${suffix || ""}`;
  },
  programLabel(
    type = "singular",
    capitalize = false,
    prefix = "",
    suffix = ""
  ) {
    if (type && typeof type === "object") {
      const opts = type || {};
      type = opts.type || "singular";
      capitalize = opts.capitalize || false;
      prefix = opts.prefix || "";
      suffix = opts.suffix || "";
    }
    let label = "program";
    try {
      const cp =
        store.state &&
        store.state.settings &&
        store.state.settings.companyProfile;
      if (
        cp &&
        cp.program_name &&
        typeof cp.program_name === "string" &&
        cp.program_name.trim() !== ""
      ) {
        label = cp.program_name.trim();
      }
    } catch (e) {
      // keep default
    }

    let result = label;
    if (type === "plural") {
      result = pluralizeLabel(result);
    }

    if (capitalize) {
      if (result.length > 0) {
        result = result.charAt(0).toUpperCase() + result.slice(1);
      }
    } else {
      if (result.length > 0) {
        result = result.charAt(0).toLowerCase() + result.slice(1);
      }
    }
    return `${prefix || ""}${result}${suffix || ""}`;
  },
  isActiveUserStatus(status) {
    return ["ACTIVE", "PROTECTED", "PENDING_BOOKING"].includes(status);
  },
};

export default filters;
